import type { ComponentType } from 'react';
import { NavLink, Outlet } from 'react-router';
import { useMe } from '../lib/queries';
import { BuildIcon, HomeIcon, LibraryIcon, SettingsIcon, ShieldIcon } from './icons';

export const TABS: { to: string; label: string; Icon: ComponentType<{ size?: number }> }[] = [
  { to: '/', label: 'Home', Icon: HomeIcon },
  { to: '/library', label: 'Library', Icon: LibraryIcon },
  { to: '/build', label: 'Create', Icon: BuildIcon },
  { to: '/settings', label: 'Settings', Icon: SettingsIcon },
];

export function Wordmark() {
  return <span className="text-[18px] font-bold tracking-[-0.01em]">ZincSudoku</span>;
}

/** Shown while /api/me loads, and when it fails for a reason other than logging in. */
export function Splash({ message }: { message?: string }) {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-3 px-6 text-center">
      <img src="/icons/icon.svg" alt="" width={64} height={64} />
      <Wordmark />
      {message && <p className="m-0 text-[14px] text-muted">{message}</p>}
    </div>
  );
}

/**
 * The four sections: bottom tabs on phones and portrait tablets, a side navigation from 1024 px
 * (desktop and landscape tablets). Admin is a separate area, linked only for the admin.
 */
export function AppShell() {
  const me = useMe();
  if (me.isPending) return <Splash />;
  if (me.isError) return <Splash message="ZincSudoku can't reach the server. Try again shortly." />;
  const isAdmin = me.data.user.role === 'admin';

  return (
    <div className="flex min-h-dvh lg:flex-row">
      <nav
        aria-label="Main"
        className="sticky top-0 hidden h-dvh w-[232px] shrink-0 flex-col gap-1 overflow-y-auto border-r border-line bg-surface px-3 py-[18px] lg:flex"
      >
        <div className="px-2 pb-3.5">
          <Wordmark />
        </div>
        {TABS.map(({ to, label, Icon }) => (
          <NavLink
            key={to}
            to={to}
            end={to === '/'}
            className={({ isActive }) =>
              `flex h-10 shrink-0 items-center gap-2.5 rounded-[9px] px-3 text-[14px] no-underline ${
                isActive
                  ? 'bg-accent-tint font-bold text-accent-dark'
                  : 'font-medium text-ink hover:text-ink'
              }`
            }
          >
            <Icon size={20} />
            {label}
          </NavLink>
        ))}
        {isAdmin && (
          <NavLink
            to="/admin"
            className="mt-auto flex h-10 shrink-0 items-center gap-2.5 rounded-[9px] px-3 text-[14px] font-medium text-subtle no-underline hover:text-ink"
          >
            <ShieldIcon size={18} />
            Admin
          </NavLink>
        )}
      </nav>

      <main className="flex min-w-0 flex-1 flex-col pb-[calc(76px+env(safe-area-inset-bottom))] lg:pb-0">
        <Outlet />
      </main>

      <nav
        aria-label="Main"
        className="fixed inset-x-0 bottom-0 z-20 box-border grid grid-cols-4 border-t border-line bg-surface px-2 pt-1.5 pb-[calc(14px+env(safe-area-inset-bottom))] lg:hidden"
        style={{ height: 'calc(76px + env(safe-area-inset-bottom))' }}
      >
        {TABS.map(({ to, label, Icon }) => (
          <NavLink
            key={to}
            to={to}
            end={to === '/'}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center gap-1 text-[11px] no-underline ${
                isActive
                  ? 'font-bold text-accent hover:text-accent'
                  : 'font-semibold text-subtle hover:text-subtle'
              }`
            }
          >
            <Icon />
            {label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

/** Pages inside the shell share this width and padding. */
export function Page({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto box-border flex w-full max-w-[720px] flex-col gap-5 px-3 pt-1 pb-6">
      {children}
    </div>
  );
}
